<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue'
import { useStore } from 'vuex'
import { useRoute } from 'vue-router'
import { WS, closeWS } from '@/services/ws'
import { toast } from '@/components/ui/toast'

const PROJECT_FEATURES_TOPIC = {
  commonName: 'projects.features',
  name(id) {
    return `${this.commonName}(${id})`
  }
}

const MONITORING_TOPIC = {
  name(id) {
    return `monitoring:${id}`
  }
}

const CONVERSATION_TOPIC = {
  name(uuid) {
    return `conversation:${uuid}`
  }
}

const MEMBER_TOPIC = {
  name(id) {
    return `member:${id}`
  }
}

const ORG_TOPIC = {
  name(id) {
    return `org:${id}`
  }
}

const route = useRoute()
const store = useStore()

const cleanUpTimer = ref(null)
const ws = ref(null)
const currentConnectedProjectId = ref(null)

const currentProjectId = computed(() => {
  if (!route.params.project_id) return null
  return parseInt(route.params.project_id)
})

function getWebSocketProtocol() {
  return location.protocol === 'https:' ? 'wss' : 'ws'
}

function connectWS() {
  if (disposed) return
  let url = `${getWebSocketProtocol()}://${window.location.host}/api/3/ws`
  if (url.includes('localhost')) {
    url = url.replace(/:\d+/, ':8000')
  }
  ws.value = WS(url, onMsg, onOpen, onClose)
}

function onMsg(topic, event, data) {
  if (!topic) return
  if (topic.startsWith('monitoring:')) {
    handleMonitoringEvent(topic, event, data)
    return
  }
  if (topic.startsWith('conversation:') || topic.startsWith('member:')) {
    store.dispatch('chat/onWsEvent', { topic, event, data })
    return
  }
  if (topic.startsWith('org:')) {
    store.dispatch('access/onWsEvent', { event, data })
    return
  }
  const truncatedTopic = truncTopic(topic)
  const handler = topicToHandlerMap[truncatedTopic]
  if (handler) handler(topic, event, data)
}

function handleMonitoringEvent(topic, event, data) {
  if (event === 'alert' && data?.title) {
    toast({ title: data.title, description: data.message })
    store.commit('monitoring/ALERT_RECEIVED', data)
  }
}

// Exponential reconnect backoff: a rejected auth closes the socket server-side,
// and an instant retry with the same token would loop forever.
const RECONNECT_BASE_MS = 500
const RECONNECT_MAX_MS = 30_000
let reconnectDelay = RECONNECT_BASE_MS
let openedAt = 0
let reconnectTimer = null
// Set on unmount. A reconnect scheduled just before teardown must not revive
// the socket behind an unmounted handler — after logout that would loop
// forever against a cleared token.
let disposed = false

// The server closes with 4001 when it refuses the auth frame. Retrying with the
// same token just loops until the backoff maxes out, and the token in storage
// may be a Firebase ID token rather than a platform one — which the server
// rejects with InvalidSignatureError, since it verifies against JWT_PUBLIC_KEY.
const WS_AUTH_REJECTED_CODE = 4001

function onClose(event) {
  clearInterval(cleanUpTimer.value)
  cleanUpTimer.value = null
  if (disposed) return

  // A connection that survived a while was healthy — start over fast.
  if (openedAt && Date.now() - openedAt > 10_000) {
    reconnectDelay = RECONNECT_BASE_MS
  }
  console.log(`WS closed, reconnecting in ${reconnectDelay}ms`)
  reconnectTimer = setTimeout(() => {
    connectWS()
  }, reconnectDelay)
  reconnectDelay = Math.min(reconnectDelay * 2, RECONNECT_MAX_MS)
}

function onOpen() {
  openedAt = Date.now()
  // Every fresh socket needs its own auth handshake — reconnects included.
  if (!ws.value.auth(localStorage.getItem('accessToken'))) {
    // No token to present: close rather than hold an unauthenticated socket
    // open. onClose then drives the refresh-and-retry path above.
    ws.value.close(WS_AUTH_REJECTED_CODE)
    return
  }
  if (currentProjectId.value) {
    connectToProject(currentProjectId.value)
  }
  subscribeChatTopics()
  if (selectedOrgId.value) {
    sub([ORG_TOPIC.name(selectedOrgId.value)])
    store.dispatch('access/refresh', selectedOrgId.value).catch(() => {})
  }
  // Broadcasts sent while the socket was down are gone — refetch the
  // conversation list and fill message gaps via after_id.
  store.dispatch('chat/catchUpAll')
  cleanUpTimer.value = setInterval(() => {
    store.commit('geo/CLEANUP_BEACONS')
  }, 1000 * 60 * 2)
}

// --- chat topics: personal topic + one topic per known conversation ---

const myMemberId = computed(() => store.getters['user/getMember']?.member_id)
const conversationUuids = computed(() => store.getters['chat/conversationUuids'])
const subscribedConversations = new Set()

function subscribeChatTopics() {
  subscribedConversations.clear()
  if (myMemberId.value) sub([MEMBER_TOPIC.name(myMemberId.value)])
  conversationUuids.value.forEach((uuid) => {
    subscribedConversations.add(uuid)
    sub([CONVERSATION_TOPIC.name(uuid)])
  })
}

watch(conversationUuids, (uuids) => {
  uuids.forEach((uuid) => {
    if (subscribedConversations.has(uuid)) return
    subscribedConversations.add(uuid)
    sub([CONVERSATION_TOPIC.name(uuid)])
  })
})

watch(myMemberId, (id, oldId) => {
  if (id && id !== oldId) sub([MEMBER_TOPIC.name(id)])
})

// The conversation list (and with it the nav unread counter) loads as soon as
// an org is selected, not only when the Chats panel opens. On org switch the
// chat store is org-scoped, so reset it and drop the old topic subscriptions.
const selectedOrgId = computed(() => store.getters['org/getSelected'])

watch(selectedOrgId, (orgId, oldOrgId) => {
  if (!orgId || orgId === oldOrgId) return
  store.dispatch('access/refresh', orgId).catch(() => {})
  if (ws.value) {
    if (oldOrgId) unsub([ORG_TOPIC.name(oldOrgId)])
    sub([ORG_TOPIC.name(orgId)])
  }
  if (oldOrgId) {
    subscribedConversations.forEach((uuid) => unsub([CONVERSATION_TOPIC.name(uuid)]))
    subscribedConversations.clear()
    store.commit('chat/RESET')
  }
  store.dispatch('chat/fetchConversations').catch(() => {})
}, { immediate: true })

function sub(topics) {
  topics.forEach(topic => {
    ws.value.subscribe(topic)
  })
}

function unsub(topics) {
  topics.forEach(topic => {
    ws.value.unsubscribe(topic)
  })
}

function truncTopic(topic) {
  return topic.split('(')[0]
}

function convertBeacon(beacon) {
  const coords = [beacon.lone6, beacon.late6]
  let rel_ts = null
  if (beacon.rel_ts.toString().length === 10) {
    rel_ts = beacon.rel_ts * 1000
  } else if (beacon.rel_ts.toString().length === 13) {
    rel_ts = beacon.rel_ts
  }

  const props = {
    id: beacon.id,
    member_id: beacon.member_id,
    type: beacon.type,
    project_id: beacon.project_id,
    member_avatar_url: beacon.member_avatar_url,
    member_display_name: beacon.member_display_name,
    rel_ts: rel_ts,
  }
  return {
    type: 'Feature',
    geometry: {
      type: 'Point',
      coordinates: coords
    },
    properties: props
  }
}

async function handleProjectEvent(topic, event, data) {
  switch (event) {
    case 'task':
      store.commit('planet/UPDATE_ORDER_STATUS', data)
      break
    case 'beacon.next':
      const geoJson = convertBeacon(data.beacon)
      store.commit('geo/NEXT_BEACON', geoJson)
      break
    case 'beacon.close':
      const memberId = data.beacon.member_id
      store.commit('geo/REMOVE_BEACON', memberId)
      break
    default:
      break
  }
}

const topicToHandlerMap = {
  [PROJECT_FEATURES_TOPIC.commonName]: handleProjectEvent
}


const connectToProject = (projectId) => {
  sub([PROJECT_FEATURES_TOPIC.name(projectId), MONITORING_TOPIC.name(projectId)])
  currentConnectedProjectId.value = projectId
}

const disconnectFromProject = (projectId) => {
  if (!projectId) return
  unsub([PROJECT_FEATURES_TOPIC.name(projectId), MONITORING_TOPIC.name(projectId)])
  currentConnectedProjectId.value = null
}

watch(
  () => currentProjectId.value,
  (newVal, oldVal) => {
    if (newVal !== currentConnectedProjectId.value) {
      if (oldVal) {
        disconnectFromProject(oldVal)
      }
      if (newVal) {
        connectToProject(newVal)
      }
    }
  }
)

onMounted(() => {
  connectWS()

  if (currentProjectId.value) {
    connectToProject(currentProjectId.value)
  }
})

onBeforeUnmount(() => {
  disposed = true
  clearInterval(cleanUpTimer.value)
  cleanUpTimer.value = null
  clearTimeout(reconnectTimer)
  reconnectTimer = null
  closeWS()
})
</script>

<template>
  <!-- This component doesn't render anything, it just handles WS communication -->
</template>
